// W08 — Státuszváltás, W09 — Elfogadás/elutasítás, W11 — Időmérés felülírása.
//
// Mindhárom ugyanazt a mintát követi: a művelet indoklása KÖTELEZŐ, és a
// változás a változásnaplóba kerül. A mentés pillanatában a hívó dönti el, mi
// történjen — a párbeszédablak csak érvényes adatot enged továbbadni.

import { t } from '../lib/i18n';
import { openDialog } from '../lib/dialog';
import { h, icon, icons } from '../lib/dom';
import {
  formatClock,
  formatDeadlineShort,
  formatElapsedClock,
  statusLabel,
  statusMeta,
  type TaskStatus,
  type TaskView,
} from '../lib/model';
import { checkboxRow, priorityTag, statusTransition } from '../lib/components';

/// Indoklás mező számlálóval.
///
/// `required` false-nál nincs csillag: a W09-en a megjegyzés csak
/// ELUTASÍTÁSNÁL kötelező, elfogadásnál nem — a csillag ott azt sugallta,
/// hogy elfogadni sem lehet nélküle.
function reasonField(options: {
  label: string;
  note?: string;
  hint?: string;
  placeholder?: string;
  maxLength?: number;
  required?: boolean;
  id?: string;
}): { element: HTMLElement; textarea: HTMLTextAreaElement; error: HTMLElement } {
  const maxLength = options.maxLength ?? 500;
  const id = options.id ?? 'reason-field';
  const required = options.required ?? true;

  const textarea = h('textarea', {
    class: 'input ta',
    rows: '4',
    maxlength: String(maxLength),
    placeholder: options.placeholder,
    id,
  }) as HTMLTextAreaElement;

  const counter = h('span', { class: 'counter', text: `0 / ${maxLength}` });
  const error = h('span', { class: 'errtx', style: { display: 'none' } });

  textarea.addEventListener('input', () => {
    counter.textContent = `${textarea.value.length} / ${maxLength}`;
    if (textarea.value.trim().length > 0) error.style.display = 'none';
  });

  return {
    textarea,
    error,
    element: h(
      'div',
      { class: 'field' },
      h(
        'label',
        { class: 'flbl', for: id },
        h('span', { text: options.label }),
        required ? h('span', { class: 'req', text: ' *' }) : null,
        options.note ? h('span', { class: 'caption', text: ` ${options.note}` }) : null,
      ),
      textarea,
      h(
        'div',
        { class: 'row between' },
        options.hint ? h('span', { class: 'hint', text: options.hint }) : h('span'),
        counter,
      ),
      error,
    ),
  };
}

/// W08 — Státusz módosítása.
///
/// A terv oldalsó fiókot mutat (400 px, jobb szél), nem középre zárt
/// párbeszédablakot: a feladatlista végig látszik mögötte.
export function openStatusDialog(options: {
  task: TaskView;
  onSubmit: (next: { status: TaskStatus; reason: string; stopTimer: boolean }) => void;
}): void {
  const { task } = options;

  // Az aktualis statuszra nem lehet valtani, az nem valtozas.
  const selectable: TaskStatus[] = (
    ['todo', 'doing', 'completed', 'failed', 'unaccepted'] as TaskStatus[]
  ).filter((s) => s !== task.status);

  let selected: TaskStatus = selectable[0]!;

  const preview = h('div');
  const choices = h('div', { class: 'row g8 wrap' });

  function draw(): void {
    preview.replaceChildren(statusTransition(task.status, selected));
    choices.replaceChildren(
      ...selectable.map((status) =>
        h(
          'button',
          {
            type: 'button',
            // A terv kattinthato jelvenyeket mutat, nem radiogombos listat.
            // A kivalasztottat keret emeli ki — csak a szinre hagyatkozni
            // nem lehet, mert a jelvenyek amugy is szinesek.
            class: `badge ${statusMeta[status].cls}`,
            'aria-pressed': status === selected ? 'true' : 'false',
            style: {
              cursor: 'pointer',
              font: 'inherit',
              fontSize: '12px',
              fontWeight: '600',
              outline: status === selected ? '2px solid var(--focus)' : 'none',
              outlineOffset: '1px',
              opacity: status === selected ? '1' : '.72',
            },
            onClick: () => {
              selected = status;
              draw();
            },
          },
          h('span', { text: statusLabel(status) }),
        ),
      ),
    );
  }
  draw();

  const reason = reasonField({
    label: t('report.reason'),
    hint: t('task.reasonHint'),
    placeholder: t('task.reasonPlaceholder'),
  });

  const stopTimer = checkboxRow({
    label: t('task.stopTimerOnSave'),
    checked: task.timerRunning,
  });

  openDialog({
    title: t('task.statusChangeTitle'),
    placement: 'right',
    width: '400px',
    body: () =>
      h(
        'div',
        { class: 'col g16' },
        h(
          'div',
          { class: 'field' },
          h('span', { class: 'flbl', text: t('task.currentStatus') }),
          preview,
        ),
        h(
          'div',
          { class: 'field' },
          h('span', { class: 'flbl', text: t('task.newStatus') }),
          choices,
        ),
        reason.element,
        task.timerRunning ? stopTimer.element : null,
      ),
    footer: (close) => [
      // A terv ket, egyenlo szelessegu gombot mutat a fiok aljan.
      h('button', {
        class: 'btn btn-secondary',
        type: 'button',
        text: t('general.cancel'),
        style: { flexGrow: '1' },
        onClick: close,
      }),
      h('button', {
        class: 'btn btn-primary',
        type: 'button',
        text: t('general.save'),
        style: { flexGrow: '1' },
        onClick: () => {
          const text = reason.textarea.value.trim();
          if (text.length === 0) {
            // Az indoklas kotelezo: e nelkul a valtozasnaplo ertelmezhetetlen.
            reason.error.textContent = t('task.reasonRequired');
            reason.error.style.display = '';
            reason.textarea.focus();
            return;
          }
          options.onSubmit({
            status: selected,
            reason: text,
            stopTimer: task.timerRunning && stopTimer.input.checked,
          });
          close();
        },
      }),
    ],
  });
}

/// W09 — Elfogadás / elutasítás.
export function openAcceptDialog(options: {
  task: TaskView;
  assignerName?: string;
  onSubmit: (result: { accepted: boolean; comment: string }) => void;
}): void {
  const { task } = options;
  const comment = reasonField({
    label: t('task.comment'),
    note: t('task.declineNoteHint'),
    required: false,
    id: 'accept-comment',
    placeholder: t('task.declinePlaceholder'),
  });

  openDialog({
    title: 'Elfogadod ezt a feladatot?',
    width: '560px',
    body: () =>
      h(
        'div',
        { class: 'col g14', style: { gap: '14px' } },
        h('span', {
          class: 'caption',
          text: t('task.waitUntilAccept'),
        }),
        // A terv kartyan mutatja, MIT fogadunk el: azonosito, cim, prioritas
        // es hatarido. Eddig csak a cim es egy osszefuzott caption allt itt.
        h(
          'div',
          { class: 'card pad16 col', style: { gap: '8px' } },
          task.sequenceNumber
            ? h('span', { class: 'mono caption', text: `#${task.sequenceNumber}` })
            : null,
          h('span', { class: 'body w6', text: task.title }),
          h(
            'div',
            { class: 'row g8 wrap' },
            task.priority ? priorityTag(task.priority) : null,
            task.deadline
              ? h(
                  'span',
                  { class: `badge ${task.isOverdue ? 'st-fail' : 'st-wait'}` },
                  h('span', { class: 'ic' }, icon(icons.calendar, 12)),
                  h('span', { text: t('task.deadlineAt', [formatDeadlineShort(task.deadline)]) }),
                )
              : null,
          ),
          options.assignerName
            ? h('span', {
                class: 'body-sm ts',
                text: [
                  `Kiosztotta: ${options.assignerName}`,
                  task.createdAt ? formatDeadlineShort(task.createdAt) : null,
                ]
                  .filter(Boolean)
                  .join(' · '),
              })
            : null,
        ),
        comment.element,
      ),
    footer: (close) => [
      // A terv sorrendje: Elutasitom · Kesobb dontok · (jobbra) Elfogadom.
      // Az elutasitas masodlagos gomb, nem piros: nem rombolo muvelet, csak
      // a masik valasz.
      h('button', {
        class: 'btn btn-secondary',
        type: 'button',
        text: t('task.declineTask'),
        onClick: () => {
          const text = comment.textarea.value.trim();
          if (text.length === 0) {
            // Elutasitasnal a megjegyzes kotelezo — a kiosztonak tudnia kell, miert.
            comment.error.textContent = t('task.declineCommentRequired');
            comment.error.style.display = '';
            comment.textarea.focus();
            return;
          }
          options.onSubmit({ accepted: false, comment: text });
          close();
        },
      }),
      h('button', {
        class: 'btn btn-ghost',
        type: 'button',
        text: t('task.decideLater'),
        onClick: close,
      }),
      h('span', { class: 'grow' }),
      h('button', {
        class: 'btn btn-primary',
        type: 'button',
        text: t('task.acceptTask'),
        onClick: () => {
          options.onSubmit({ accepted: true, comment: comment.textarea.value.trim() });
          close();
        },
      }),
    ],
  });
}

/// W11 — Időmérés felülírása.
export function openTimeOverrideDialog(options: {
  task: TaskView;
  onSubmit: (result: { seconds: number; reason: string }) => void;
}): void {
  const { task } = options;

  /// A terv három, egymás melletti, KÖZÉPRE ZÁRT mezőt mutat, alattuk a
  /// mértékegységgel. Korábban három sima, 96 px-es számmező állt egymás
  /// mellett, a címkével fölöttük — a „0 / 03 / 30" olvasata nem derült ki.
  const unitInput = (label: string, initial: string, max?: number) => {
    const input = h('input', {
      class: 'input mono',
      type: 'number',
      inputmode: 'numeric',
      min: '0',
      max: max !== undefined ? String(max) : undefined,
      value: initial,
      'aria-label': label,
      style: { textAlign: 'center', fontSize: '20px', height: '56px' },
    }) as HTMLInputElement;

    return {
      input,
      element: h(
        'div',
        { class: 'col', style: { gap: '4px', flexGrow: '1', minWidth: '0' } },
        input,
        h('span', { class: 'caption', style: { textAlign: 'center' }, text: label }),
      ),
    };
  };

  // A mezok a MOSTANI mert idovel indulnak, nem nullaval: a felulirasnal
  // szinte mindig egy meglevo erteket igazit valaki, nem nullarol ir be
  // mindent. Nullarol indulva a mentes gomb ugyanugy hibat dobott volna.
  const total = Math.max(0, Math.round(task.elapsedSeconds));
  const startDays = Math.floor(total / 86400);
  const startHours = Math.floor((total % 86400) / 3600);
  const startMinutes = Math.floor((total % 3600) / 60);

  const days = unitInput(t('people.unitDay'), String(startDays));
  const hours = unitInput(t('people.unitHour'), String(startHours).padStart(2, '0'), 23);
  const minutes = unitInput(t('people.unitMinute'), String(startMinutes).padStart(2, '0'), 59);

  const reason = reasonField({
    label: t('report.reason'),
    id: 'override-reason',
    placeholder: t('task.rewriteReasonPlaceholder'),
  });

  const totalError = h('span', { class: 'errtx', style: { display: 'none' } });

  openDialog({
    title: t('task.rewriteTitle'),
    width: '520px',
    body: () =>
      h(
        'div',
        { class: 'col g16' },
        task.sequenceNumber
          ? h('span', { class: 'caption mono', text: `#${task.sequenceNumber}` })
          : null,
        // A terv nagy, egybeolvasható óraszámot mutat a kártyán.
        h(
          'div',
          { class: 'card pad16 row between', style: { alignItems: 'center' } },
          h(
            'span',
            { class: 'col', style: { gap: '0' } },
            h('span', { class: 'label', text: t('report.colTracked') }),
            h('span', {
              class: 'mono w7',
              style: { fontSize: '30px' },
              text: formatElapsedClock(task.elapsedSeconds),
            }),
          ),
          task.timerRunning
            ? h(
                'span',
                { class: 'badge st-prog' },
                h('span', { class: 'ic' }, icon(icons.timer, 12)),
                h('span', {
                  text: task.timerStartedAt
                    ? t('task.runningSince', [formatClock(task.timerStartedAt)])
                    : 'Fut',
                }),
              )
            : null,
        ),
        h(
          'div',
          { class: 'field' },
          h('span', { class: 'flbl', text: t('task.rewrittenDuration') }),
          h('div', { class: 'row g8' }, days.element, hours.element, minutes.element),
        ),
        totalError,
        reason.element,
        h('span', {
          class: 'caption',
          text:
            t('task.rewriteNote'),
        }),
      ),
    footer: (close) => [
      h('span', { class: 'grow' }),
      h('button', { class: 'btn btn-secondary', type: 'button', text: t('general.cancel'), onClick: close }),
      h('button', {
        class: 'btn btn-primary',
        type: 'button',
        text: t('task.saveRewrite'),
        onClick: () => {
          const seconds =
            Number(days.input.value || 0) * 86400 +
            Number(hours.input.value || 0) * 3600 +
            Number(minutes.input.value || 0) * 60;

          if (seconds <= 0) {
            // Nulla idotartam felulirasnak ertelmetlen — az idomeres torlese
            // kulon muvelet lenne.
            totalError.textContent = t('task.needPositiveDuration');
            totalError.style.display = '';
            days.input.focus();
            return;
          }

          const text = reason.textarea.value.trim();
          if (text.length === 0) {
            reason.error.textContent = t('task.reasonRequired');
            reason.error.style.display = '';
            reason.textarea.focus();
            return;
          }

          options.onSubmit({ seconds, reason: text });
          close();
        },
      }),
    ],
  });
}
